/* eslint-disable indent */
/* eslint-disable react/jsx-indent */
import React, { useEffect, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { MdOutlineAddBox } from 'react-icons/md';
import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { DELETE_FEATURE_FLAG, GET_ALL_FEATURE_FLAGS } from '../../utils/constants';
import { getFirebaseIdToken } from '../../utils/utils';
import CreateFeatureFlags from './create-feature-flags';
import FeatureFlagCard, { FeatureFlag } from './feature-flag-card.component';
import GetFeatureFlags from './get-feature-flags';

const FeatureFlagsScreen = () => {
  const toast = useToast();
  const [featureFlags, setFeatureFlags] = useState([]);
  const [filteredFlags, setFilteredFlags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentScreen, setCurrentScreen] = useState('listAllFlags');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFlag, setSelectedFlag] = useState<FeatureFlag | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (currentScreen === 'listAllFlags') {
      getFeatureFlags();
    }
  }, [currentScreen]);

  useEffect(() => {
    if (searchQuery) {
      const filteredFlags = featureFlags.filter((flag: any) =>
        flag?.name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredFlags(filteredFlags);
    } else {
      setFilteredFlags(featureFlags);
    }
  }, [searchQuery, featureFlags]);

  const getFeatureFlags = async () => {
    try {
      setLoading(true);
      setError('');
      const accessToken = await getFirebaseIdToken();
      const response = await fetch(GET_ALL_FEATURE_FLAGS, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Something went wrong while fetching feature flags');
      }

      const data = await response.json();
      setFeatureFlags(data);
      setFilteredFlags(data);
    } catch (error) {
      console.log({ error });
      setError('Something went wrong while fetching feature flags');
      toast({
        title: 'Error',
        description: 'Failed to fetch feature flags',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFeatureFlag = async (flagName: string) => {
    try {
      const accessToken = await getFirebaseIdToken();
      const response = await fetch(DELETE_FEATURE_FLAG(flagName), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) {
        throw new Error('Something went wrong while deleting feature flag');
      }

      getFeatureFlags();
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  return (
    <Flex direction="column" w="98%" maxW="1300px" margin="auto" mt={3}>
      <Card>
        <CardBody p={0}>
          <Flex
            direction="column"
            gap={5}
            align="stretch"
            pb={0}
            p={7}
            h={`calc(100vh - ${currentScreen === 'listAllFlags' ? '120px' : '180px'})`}
            overflowY="scroll"
            overflowX="hidden"
          >
            <Box width="100%">
              <VStack align="flex-start" spacing="16px" w="100%">
                <VStack align="flex-start" spacing="4px">
                  <Text
                    fontFamily="inter"
                    className="medium-font"
                    fontWeight="bold"
                    color="black"
                  >
                    Feature Flags
                  </Text>
                  <Text
                    color="gray.600"
                    className="small-font"
                    whiteSpace="pre-line"
                    fontFamily="inter"
                  >
                    This helps you to control the flow of your application.
                  </Text>
                </VStack>
                <HStack spacing="20px" w="100%" justifyContent="space-between">
                  <InputGroup maxW="300px">
                    <InputLeftElement pointerEvents="none">
                      <FiSearch color="gray" style={{ marginBottom: 5 }} />
                    </InputLeftElement>
                    <Input
                      size="md"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      type="search"
                      placeholder="Search flags"
                      className="small-font"
                      borderRadius="8px"
                      variant="filled"
                      bgColor="#f3f5fc"
                      isDisabled={currentScreen !== 'listAllFlags' || loading}
                    />
                  </InputGroup>
                  <Flex alignItems="center" gap={5}>
                    <Button
                      leftIcon={<MdOutlineAddBox size={18} color="black" />}
                      className="small-font"
                      borderRadius="8px"
                      onClick={() => setCurrentScreen('searchFeatureFlags')}
                      variant="outline"
                      size="md"
                      fontWeight={500}
                    >
                      Run Query
                    </Button>
                    <Button
                      leftIcon={<MdOutlineAddBox size={18} color="black" />}
                      className="small-font"
                      borderRadius="8px"
                      onClick={() => setCurrentScreen('createNewFlag')}
                      variant="outline"
                      size="md"
                      fontWeight={500}
                    >
                      Create New Flag
                    </Button>
                  </Flex>
                </HStack>
              </VStack>
            </Box>
            {loading ? (
              <Flex w="full" h="full" justifyContent="center" alignItems="center">
                <Spinner size="lg" />
              </Flex>
            ) : (
              <Box>
                {currentScreen === 'listAllFlags' && (
                  <FeatureFlagCard
                    error={error}
                    featureFlags={filteredFlags}
                    setCurrentScreen={setCurrentScreen}
                    setSelectedFlag={setSelectedFlag}
                    onDeleteFlag={handleDeleteFeatureFlag}
                  />
                )}
                {currentScreen === 'createNewFlag' && (
                  <CreateFeatureFlags
                    setCurrentScreen={setCurrentScreen}
                    selectedFlag={selectedFlag}
                  />
                )}
                {currentScreen === 'searchFeatureFlags' && <GetFeatureFlags />}
              </Box>
            )}
          </Flex>
          {currentScreen !== 'listAllFlags' && (
            <Flex
              w="100%"
              h="70px"
              alignItems="center"
              backgroundColor="gray.50"
              borderRadius="inherit"
              borderTopLeftRadius="0px"
              borderTopRightRadius="0px"
              px={30}
            >
              <Button
                backgroundColor="transparent"
                border="1px solid gray"
                borderRadius={10}
                fontWeight="normal"
                _hover={{
                  fontWeight: 'bold',
                  transform: 'scale(1.05)',
                }}
                onClick={() => setCurrentScreen('listAllFlags')}
              >
                Back
              </Button>
            </Flex>
          )}
        </CardBody>
      </Card>
    </Flex>
  );
};

export default FeatureFlagsScreen;
